import { useT, type StringId } from '../lib/i18n'

export type MascotState = 'idle' | 'happy' | 'thinking' | 'resting' | 'curious'

interface MascotProps {
  state?: MascotState
  size?: number
  /** Decorative next to copy that already says it. Defaults to labelled. */
  decorative?: boolean
}

const LABELS: Record<MascotState, StringId> = {
  idle: 'mascot.idle',
  happy: 'mascot.happy',
  thinking: 'mascot.thinking',
  resting: 'mascot.resting',
  curious: 'mascot.curious',
}

/**
 * The coaching voice, as a shape rather than a face.
 *
 * **No human bodies in this UI, anywhere.** That is the rule this component
 * exists to satisfy: a body-image-sensitive product should not show anyone a
 * body, and a mascot can carry encouragement without implying a target physique.
 * A blob has no weight, no build and nothing to compare yourself to.
 *
 * **This is placeholder geometry and it is deliberately obvious.** The real
 * character is commissioned art (`SUAP-0108` § Out of scope). The dashed outline
 * and the `data-mascot="placeholder"` attribute are both load-bearing: the
 * outline stops anyone shipping it thinking it is finished, and the attribute
 * lets `check:kit` assert that no product route renders it. Replacing the art
 * means replacing the paths below and deleting both markers — in that order.
 *
 * Animation is CSS-only and every state's animation is `motion-safe:`, so
 * `prefers-reduced-motion` leaves a still drawing rather than a subtler one.
 *
 * SUAP-0108.
 */
export function Mascot({ state = 'idle', size = 96, decorative = false }: MascotProps) {
  const { t } = useT()

  // One blob, five expressions. Keeping the body identical across states is what
  // makes it read as the same character rather than five drawings.
  const eyes = {
    idle: { rx: 5, ry: 6, cy: 44 },
    happy: { rx: 5, ry: 3, cy: 43 },
    thinking: { rx: 4, ry: 6, cy: 43 },
    // Closed: a flat ellipse reads as a shut eye without needing a second shape.
    resting: { rx: 6, ry: 1, cy: 45 },
    curious: { rx: 6, ry: 7, cy: 43 },
  }[state]

  const mouth = {
    idle: 'M 42 60 q 14 6 28 0',
    happy: 'M 40 58 q 16 14 32 0',
    thinking: 'M 44 62 q 14 0 24 -2',
    resting: 'M 46 62 q 10 3 20 0',
    curious: 'M 48 60 q 8 8 16 0',
  }[state]

  const animation = {
    idle: 'motion-safe:animate-mascot-breathe',
    happy: 'motion-safe:animate-mascot-bounce',
    thinking: 'motion-safe:animate-mascot-tilt',
    resting: 'motion-safe:animate-mascot-rest',
    curious: 'motion-safe:animate-mascot-tilt',
  }[state]

  return (
    <span
      data-mascot="placeholder"
      data-state={state}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : t(LABELS[state])}
      aria-hidden={decorative ? true : undefined}
      className="inline-flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 112 112" width={size} height={size} className={animation} aria-hidden>
        {/* The body is ink, not the coral — the design system's own rule rather
            than a workaround. The accent is reserved for the one thing on a
            screen worth shouting about, and a mascot is never that.
            The first draft used the coral as a shape fill and `check:accent`
            rejected it. That guard deliberately does not distinguish an SVG
            shape-fill from an icon colour — `index.css` says so — and on
            inspection it was pointing at a real design mistake rather than a
            false positive, so the drawing changed rather than the rule. */}
        <path
          d="M 56 14 C 82 14 98 32 98 58 C 98 84 80 98 56 98 C 32 98 14 84 14 58 C 14 32 30 14 56 14 Z"
          className="fill-ink"
        />
        {/* The placeholder marker: a dashed ring nobody would ship on purpose. */}
        <path
          d="M 56 14 C 82 14 98 32 98 58 C 98 84 80 98 56 98 C 32 98 14 84 14 58 C 14 32 30 14 56 14 Z"
          fill="none"
          strokeWidth="2"
          strokeDasharray="6 5"
          className="stroke-ground"
          opacity="0.55"
        />
        <ellipse cx="44" cy={eyes.cy} rx={eyes.rx} ry={eyes.ry} className="fill-ground" />
        <ellipse cx="68" cy={eyes.cy} rx={eyes.rx} ry={eyes.ry} className="fill-ground" />
        <path d={mouth} fill="none" strokeWidth="3" strokeLinecap="round" className="stroke-ground" />
        {/* Thinking gets three dots; every other state gets nothing, rather than
            a shape that means nothing.

            Every feature here is `fill-ground` — it is a hole punched in the body,
            not a mark drawn on top of it. So a feature outside the body path is
            ground-on-ground and simply invisible, which is how the first two of
            these three dots shipped: they sat at (89,24) and (96,19), and the
            body's top-right curve passes through (86.75, 26.25) at t=0.5. The
            blob is very nearly a circle of radius 42.5 about (56,56) in this
            quadrant, so the rule for any new feature is
            `dist((x,y),(56,56)) + r <= 41`. `ui:mascot` now asserts exactly this
            with `isPointInFill`, because byte-counting could not see it. */}
        {state === 'thinking' ? (
          <g className="fill-ground motion-safe:animate-mascot-think">
            <circle cx="76" cy="36" r="2.8" />
            <circle cx="80" cy="32" r="2.1" />
            <circle cx="83" cy="29" r="1.5" />
          </g>
        ) : null}
      </svg>
    </span>
  )
}
